import { FC, useContext, useState } from 'react';

import { H2, Preloader, Tabs, Tooltip as TooltipKit } from '@smartshell/ui-kit';
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { LocalizationContext } from 'localization';

import { RevenueTooltip } from '../Revenue/RevenueTooltip';
import s from './ChartClubLoad.module.scss';
import { IChartClubLoadProps } from './interfaces';

export const ChartClubLoad: FC<IChartClubLoadProps> = ({ data, loading }) => {
  const { t } = useContext(LocalizationContext);

  const [focusBar, setFocusBar] = useState<number | null>(null);
  const [selectedDay, setSelectedDay] = useState<number>(1);

  const formatWeekday = (str: string): string => {
    if (str.length >= 2) {
      return str[0] + str[1].toLowerCase() + (str.slice(2) || '');
    }
    return str;
  };

  const OPTIONS = [
    { title: formatWeekday(t('weekdays.monday')), value: 1 },
    { title: formatWeekday(t('weekdays.tuesday')), value: 2 },
    { title: formatWeekday(t('weekdays.wednesday')), value: 3 },
    { title: formatWeekday(t('weekdays.thursday')), value: 4 },
    { title: formatWeekday(t('weekdays.friday')), value: 5 },
    { title: formatWeekday(t('weekdays.saturday')), value: 6 },
    { title: formatWeekday(t('weekdays.sunday')), value: 7 },
  ];

  const chartData = data
    ?.filter((item) => item.values[0] === selectedDay)
    ?.sort((a, b) => a.values[1] - b.values[1])
    ?.map((item) => ({
      name: `${item.values[1]}`,
      value: item.values[2],
    }));

  return (
    <div className={s.container}>
      <div className={s.top}>
        <H2 className={s.title}>{t('visitors.schedule.club')}</H2>
        <TooltipKit
          contentClassName={s.tooltip}
          content={t('visitors.schedule.club.tooltip.footer')}
        />
      </div>
      {!loading && data === undefined ? (
        <div className={s.placeholder}>
          {t('report.placeholder.emptyDates')}
        </div>
      ) : (
        <Preloader loading={loading}>
          <Tabs
            options={OPTIONS}
            onChange={(value) => {
              setSelectedDay(value);
            }}
            value={selectedDay}
            className={s.tabs}
          />
          <ResponsiveContainer className={s.data} width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                left: 0,
              }}
              onMouseMove={(state) => {
                if (state.isTooltipActive) {
                  setFocusBar(state.activeTooltipIndex ?? null);
                } else {
                  setFocusBar(null);
                }
              }}
            >
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'var(--text-primary)' }}
                fontSize={12}
              />
              <YAxis
                tickFormatter={(value) => `${value} ${t('pc')}`}
                axisLine={false}
                tickLine={false}
                tick={{
                  fill: 'var(--text-primary)',
                  style: { whiteSpace: 'nowrap' },
                }}
                fontSize={12}
                allowDecimals={false}
              />
              <Tooltip
                cursor={{ fill: 'var(--bg-item-secondary)' }}
                content={({ payload }) => {
                  const rawValue = payload?.[0]?.value;
                  const value = typeof rawValue === 'number' ? rawValue : 0;
                  const currentHour = Number(payload?.[0]?.payload?.name);
                  const nextHour = currentHour < 23 ? currentHour + 1 : 0;
                  return (
                    <RevenueTooltip
                      title={`${currentHour}:00 — ${nextHour}:00`}
                      text_1={t('visitors.schedule.club.tooltip.title')}
                      value_1={`${value}`}
                      variant="primary"
                    />
                  );
                }}
              />
              <Bar
                fill="var(--bg-blue-10)"
                dataKey="value"
                radius={[4, 4, 0, 0]}
                animationDuration={400}
                animationEasing="ease-in-out"
                isAnimationActive={true}
              >
                {chartData?.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      focusBar === index
                        ? 'var(--bg-blue)'
                        : 'var(--bg-blue-10)'
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Preloader>
      )}
    </div>
  );
};
